import type { UserLocals } from '$lib/interfaces/user';
import { sanitizeNumber } from './number';

export const generateRandomString = (length: number): string => {
	let result = '';
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
		counter += 1;
	}
	return result;
};

export const generatePrintOrderKeyString = (email: string, user: string) => {
	return String(`${email}_${user}_print_order`);
};

export const generatePrintReceiptKeyString = (email: string, user: string) => {
	return String(`${email}_${user}_print_receipt`);
};

export const jsEscape = (str: string) => {
	return String(str).replace(/[^\w. ]/gi, function (c) {
		return '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4);
	});
};

export const queryEscape = (str: string) => {
	return String(str).replace(/[^\w.|-]/gi, '');
};

export const stringLike = (str: string) => {
	return new RegExp(`.*${str}.*`, 'i');
};

export const isContainDuplicateString = (strArray: string[]): boolean => {
	return new Set(strArray).size !== strArray.length;
};

export const isAllSameString = (arr: string[]): boolean => {
	if (arr.length === 0) {
		return false; // If the array is empty, return false
	}
	const firstString = arr[0]; // Get the first string in the array
	for (let i = 1; i < arr.length; i++) {
		if (arr[i] !== firstString) {
			return false; // If any string is different, return false
		}
	}
	return true; // All strings are the same
};

export const isValidJSONArray = (str: string) => {
	if (typeof str !== 'string') {
		return false;
	}
	try {
		const parsedValue = JSON.parse(str);
		return Array.isArray(parsedValue);
	} catch (error) {
		return false;
	}
};

export const escapeRegex = (str: string) => {
	return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const extractErrorMessages = (str: string): string => {
	str = str.replace(/"/g, '');
	const strArray = str.split('\\n');
	let result = '';

	for (let i = 0; i < strArray.length; i++) {
		const errorMessage = strArray[i].split(': ')[1].trim();
		result += `${errorMessage}`;
		if (i < strArray.length - 1) result += `/ `;
	}

	result = result.trim();

	return result;
};

export const getFormattedNumberFromExcel = (rawNumber: string): number => {
	if (!rawNumber) return 0;
	return sanitizeNumber(rawNumber.replaceAll('.00', ''));
};

export const generateProductsKeyRedis = (user: UserLocals) => {
	return 'products_' + user?.email + '_' + user?.access?.company?._id;
};

export const generateRedisKey = (categoryName: string, user: UserLocals) => {
	return categoryName + '_' + user?.access?.company?._id;
};

export const formatPhoneNumber = (str: string): string => {
	// Remove spaces, dashes, and pluses
	let cleaned = str.replace(/[\s\-+]/g, '');

	// If the number starts with '0', replace it with '62'
	if (cleaned.startsWith('0')) {
		cleaned = '62' + cleaned.slice(1);
	}
	// If the number starts with '812' (without '0'), prefix it with '62'
	else if (cleaned.startsWith('8')) {
		cleaned = '62' + cleaned;
	}
	// If the number starts with '+62', remove the '+'
	else if (cleaned.startsWith('+62')) {
		cleaned = cleaned.replace('+', '');
	}
	// If the number starts with '62' but not '628', ensure it correctly starts with '62'
	else if (cleaned.startsWith('62') && !cleaned.startsWith('628')) {
		cleaned = '62' + cleaned.slice(2);
	}
	// Whatever
	else if (!cleaned.startsWith('62')) {
		cleaned = '62' + cleaned;
	}

	return cleaned;
};

export const generateCategoriesParentListRedisKey = (user: UserLocals) => {
	return 'categories_parent_list_' + user.access?.company?._id;
};

export const limitString = (str: string, limit: number) => {
	return str.length > limit ? str.substring(0, limit) + '...' : str;
};

export const isNullOrEmptyString = (val: unknown) => {
	return val === null || val === '' || val === undefined;
};

export const isNullOrEmptyArray = (val: Array<unknown> | undefined | null) => {
	return val === null || val === undefined || val.length === 0;
};

export const censorWords = (text: string) => {
	let result = '';
	for (const char of text) {
		if (char !== ' ') result += '*';
		else result += ' ';
	}
	return result;
};

export const base64ToBlob = (base64: string, mime = 'image/png'): Blob => {
	const byteString = atob(base64.split(',')[1]);
	const ab = new ArrayBuffer(byteString.length);
	const ia = new Uint8Array(ab);
	for (let i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	}
	return new Blob([ab], { type: mime });
};
